import { Checkbox, Loader } from '@mantine/core';
import { usePrevious } from '@mantine/hooks';
import { IconCircleCheckFilled, IconCircleXFilled } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { activeStepAtom, expandOrCollapseAllAtom, selectedStepsIdsAtom } from '~/atoms/no-code-atoms';
import { completedItemsAtom } from '~/atoms/recorder-atoms';
import { isStepSeparating } from '~/hooks/mutations/optimistic-handles';
import { useStepOpsToSteps } from '~/hooks/mutations/steps/useStepOpsToSteps';
import { useUpdateStepDescription } from '~/hooks/mutations/steps/useUpdateStepDescription';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { SelectedStep } from '~/server/logic/prisma/step-select';

export default function StepStatusAndSelect({
  step,
  idx,
  testContentId,
  selected,
  readonly,
  setExpanded,
  setPrevDesc,
}: {
  step: SelectedStep;
  idx: number;
  testContentId: string;
  selected?: boolean;
  readonly?: boolean;
  setExpanded: (expanded: boolean) => void;
  setPrevDesc: (prevDesc: string | undefined) => void;
}) {
  const { vmStatus, recorderState } = useLiveStateManager();
  const [selectedStepsIds, setSelectedStepsIds] = useAtom(selectedStepsIdsAtom);
  const [expandOrCollapseAll, setExpandOrCollapseAll] = useAtom(expandOrCollapseAllAtom);
  const completedItems = useAtomValue(completedItemsAtom);
  const activeStep = useAtomValue(activeStepAtom);

  const replayingStatus = useMemo(() => {
    if (vmStatus?.state === 'replaying') {
      const isReplayingOp =
        step.operations.some(
          (op) =>
            op.id === vmStatus?.executionState?.operationId ||
            op.id === vmStatus?.executionState?.stepSourceOperationId,
        ) ||
        // Hack: we hard-code ai step step-ids as operation ids
        vmStatus.executionState?.operationId === step.id;
      if (isReplayingOp) {
        return 'replaying';
      }

      if (completedItems.completedStepIds.includes(step.id)) {
        return 'success';
      }
    } else if (vmStatus?.state === 'error') {
      const isReplayingOp =
        step.operations.some(
          (op) =>
            op.id === vmStatus?.executionState?.operationId ||
            op.id === vmStatus?.executionState?.stepSourceOperationId,
        ) ||
        // Hack: we hard-code ai step step-ids as oepration ids
        vmStatus.executionState?.operationId === step.id;
      if (isReplayingOp) {
        return 'fail';
      }
    }
    return undefined;
  }, [
    step.id,
    step.operations,
    completedItems.completedStepIds,
    vmStatus?.executionState?.operationId,
    vmStatus?.executionState?.stepSourceOperationId,
    vmStatus?.state,
  ]);

  // ### Handle expanded state ###
  // Expand step when selected
  useEffect(() => {
    if (selected) {
      setExpanded(true);
    }
  }, [selected, setExpanded]);
  // Expand step when active
  useEffect(() => {
    if (activeStep?.id === step.id) {
      setExpanded(true);
    }
  }, [activeStep, setExpanded, step.id]);
  // Expand when replaying current step
  useEffect(() => {
    if (replayingStatus === 'replaying') {
      setExpanded(true);
    }
  }, [replayingStatus, setExpanded]);
  // Expand when expanding/collapsing all control used
  useEffect(() => {
    if (expandOrCollapseAll !== undefined) {
      setExpanded(expandOrCollapseAll === 'expand');
    }
  }, [expandOrCollapseAll, setExpanded]);

  const { updateStepDescription } = useUpdateStepDescription({
    testContentId,
    callbacks: {
      onSuccess: () => {
        if (step.description.toLowerCase() !== '' && step.description.toLowerCase() !== 'new step') {
          setPrevDesc(step.description);
        }
      },
    },
  });

  const isPolishing = isStepSeparating(step.id);

  const { mutate: stepOpsToSteps } = useStepOpsToSteps({ testContentId, stepId: step.id });

  const wasRecording = usePrevious(recorderState?.stepId === step.id && recorderState?.executionType === 'record');
  const isRecording = recorderState?.stepId === step.id && recorderState?.executionType === 'record';

  // This triggers a generation of the step description or sometimes even breaks the step into multiple steps
  // TODO: refactor to move it to a better place
  const recordingOrPreviewing = recorderState !== undefined;
  useEffect(() => {
    // Only polish new steps
    if (step.description.toLowerCase() !== '') {
      return;
    }
    if (step.mode === 'AUTH') {
      // Don't polish auth steps
      return;
    }
    if (step.operations.length === 0) {
      // No ops, do nothing
      return;
    }
    if (step.mode === 'AI_DYNAMIC') {
      // Don't break up AI dynamic steps
      return;
    }
    if (wasRecording && (!recordingOrPreviewing || !isRecording) && !isPolishing) {
      if (step.mode === 'STRICT' || step.operations.length <= 6 || recordingOrPreviewing) {
        // Don't break up strict steps or steps with few ops
        updateStepDescription({ stepId: step.id }).catch(() => undefined);
      } else {
        // More than 6 ops, break up the steps - only if recording stopped completely
        // Only stop this after the step is created
        if (!recordingOrPreviewing) {
          stepOpsToSteps({
            stepId: step.id,
          });
        }
      }
    }
  }, [wasRecording, isRecording, isPolishing, stepOpsToSteps, step.id, step.operations.length, updateStepDescription]);

  return (
    <div className="flex h-5 w-5 items-center justify-center">
      {/* Only show replaying when recorder/previewer open */}
      {replayingStatus === 'replaying' && !!recorderState ?
        <Loader
          className="flex items-center justify-center"
          color="blue"
          size={14}
        />
      : replayingStatus === 'success' ?
        <IconCircleCheckFilled
          className="text-green-600"
          size={16}
        />
      : replayingStatus === 'fail' ?
        <IconCircleXFilled
          className="text-red-500 dark:text-red-400"
          size={16}
        />
      : !readonly ?
        <Checkbox
          size="xs"
          className={clsx({
            'opacity-0 transition-opacity duration-200 group-hover:opacity-100': true,
            'opacity-100': selected,
          })}
          data-testid={`step-checkbox-${idx}`}
          checked={selectedStepsIds.includes(step.id)}
          disabled={isPolishing}
          onChange={(event) => {
            setSelectedStepsIds((prev) =>
              event.currentTarget.checked ? [...prev, step.id] : prev.filter((id) => id !== step.id),
            );
            setExpandOrCollapseAll(undefined);
          }}
        />
      : null}
    </div>
  );
}
